export type AppDownloadPlatformId = 'windows' | 'linux' | 'macos_arm' | 'macos_x64';

/** Prefix a relative download/API path with `window.countryServer` (same as `WebStorage.ApiRequest`). */
export const resolveApplicationDownloadUrl = (path: string) => {
    if (path.includes('https://')) {
        return path;
    }

    const base = String(
        (globalThis as { countryServer?: string }).countryServer || '',
    ).replace(/\/+$/, '');
    const normalized = path.startsWith('/') ? path : `/${path}`;

    return `${base}${normalized}`;
};

export type AppDownloadFile = {
    filename: string;
    size?: number;
    downloadUrl: string;
};

export type AppDownloadPlatform = {
    id: AppDownloadPlatformId;
    version: string | null;
    files: AppDownloadFile[];
};

export type AppDownloadBranch = {
    id: string;
    protected: boolean;
    accessible: boolean;
    platforms: AppDownloadPlatform[];
};

export type AppDownloadManifest = {
    cachedAt: number;
    branches: AppDownloadBranch[];
};

/** Website download modal and public manifest: Stable first, then Beta, then Develop. */
export const APP_DOWNLOAD_BRANCH_ORDER = ['main', 'beta', 'develop'] as const;

export const compareAppDownloadBranchIds = (a: string, b: string) => {
    const order = APP_DOWNLOAD_BRANCH_ORDER as readonly string[];
    const aOrder = order.indexOf(a);
    const bOrder = order.indexOf(b);

    return (aOrder === -1 ? order.length : aOrder) - (bOrder === -1 ? order.length : bOrder);
};

/** Prefer Stable (`main`) whenever it is in the list, even if the API sent another branch first. */
export const pickDefaultAppDownloadBranchId = (branchIds: readonly string[]) => {
    if (branchIds.includes('main')) {
        return 'main';
    }

    return branchIds[0] || '';
};

export type AppUpdaterAdminAsset = {
    name: string;
    size: number;
    cached: boolean;
    downloadUrl: string;
};

export type AppUpdaterAdminBranch = {
    id: string;
    url: string;
    repo: string | null;
    protected: boolean;
    githubVersion: string | null;
    githubTag: string | null;
    githubError: string | null;
    cachedVersion: string | null;
    currentVersion: string | null;
    syncedAt: number | null;
    complete: boolean;
    assets: AppUpdaterAdminAsset[];
};

export type AppUpdaterAdminStatus = {
    scanIntervalMs: number;
    lastScanAt: number | null;
    scanRunning: boolean;
    branches: AppUpdaterAdminBranch[];
};

export type AppUpdaterOidcCaller = {
    repo: string;
    workflows: string[];
};

export type AppUpdaterOidcAdminData = {
    callers: AppUpdaterOidcCaller[];
    endpoint: string;
    audience: string;
    example: string;
};
